import type { Task } from '@nexo/types';
import { AvatarStack, cn } from '@nexo/ui';
import { AlignLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTeam } from '../../layouts/TeamContext';
import { PointsBadge, PriorityIcon, StatusButton, TaskKey, TypeIcon } from './TaskIcons';
import { ChannelBadge, DueBadge, ChecklistBadges } from './TaskMeta';
import { useUpdateTask } from './useTaskMutations';

/** Card used by the board view. */
export function TaskCard({ task }: { task: Task }) {
  const { t } = useTranslation();
  const { openTask } = useTeam();
  const update = useUpdateTask();
  const done = task.status === 'done';
  return (
    <article
      onClick={() => openTask({ mode: 'edit', taskId: task.id })}
      className="group cursor-pointer rounded-xl border border-border bg-surface p-3 shadow-panel hover-lift hover:border-border-strong"
    >
      <div className="flex items-start gap-2.5">
        <span className="pt-0.5">
          <StatusButton
            status={task.status}
            onChange={(status) => update.mutate({ id: task.id, input: { status } })}
          />
        </span>
        <p
          className={cn(
            'min-w-0 flex-1 text-sm leading-snug font-medium',
            done && 'text-subtle line-through',
          )}
        >
          {task.title}
        </p>
        <PriorityIcon priority={task.priority} className="mt-0.5" />
      </div>
      <div className="mt-2.5 flex flex-wrap items-center gap-1.5 pl-7">
        <TypeIcon type={task.type} />
        <TaskKey task={task} />
        {task.description && (
          <AlignLeft className="size-3.5 text-subtle" aria-label={t('Tiene descripción')} />
        )}
        <ChannelBadge channelId={task.channelId} />
        {!done && <DueBadge task={task} />}
        <ChecklistBadges task={task} />
        <span className="ml-auto flex items-center gap-1.5">
          <PointsBadge points={task.storyPoints} />
          {task.assignees.length > 0 && <AvatarStack people={task.assignees} />}
        </span>
      </div>
    </article>
  );
}
